import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Diamond } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const dt = (v) => (v ? String(v).slice(0, 10) : '—');
const parse = (v) => (v ? new Date(String(v).slice(0, 10)).getTime() : null);
const STATUS_BAR = {
  NOT_STARTED: 'bg-slate-400', IN_PROGRESS: 'bg-blue-500', COMPLETED: 'bg-emerald-500',
  ON_HOLD: 'bg-amber-500', UNKNOWN: 'bg-slate-300',
};

// Lightweight schedule visualization (NOT a CPM authoring tool). Desktop = horizontal Gantt,
// mobile = focused date-ordered list. Renders a window of activities for performance.
const ScheduleTimeline = ({ projectId, scheduleId, onOpenActivity }) => {
  const [acts, setActs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!scheduleId) { setLoading(false); return; }
    setLoading(true);
    axios.get(`${API}/projects/${projectId}/schedules/${scheduleId}/activities`, { params: { page: 1, pageSize: 150 } })
      .then((r) => setActs(r.data.items || []))
      .catch(() => toast.error('Failed to load timeline'))
      .finally(() => setLoading(false));
  }, [projectId, scheduleId]);

  if (loading) return <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  if (acts.length === 0) return <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-400" data-testid="timeline-empty">No activities to visualize.</div>;

  const dated = acts.filter((a) => parse(a.plannedStart) && parse(a.plannedFinish));
  const starts = dated.map((a) => parse(a.plannedStart));
  const finishes = dated.map((a) => parse(a.plannedFinish));
  const min = starts.length ? Math.min(...starts) : Date.now();
  const max = finishes.length ? Math.max(...finishes) : Date.now() + 86400000;
  const span = Math.max(1, max - min);
  const pct = (t) => `${((t - min) / span) * 100}%`;

  return (
    <div data-testid="schedule-timeline">
      <p className="text-xs text-slate-400 mb-3">Visualization of up to 150 activities ({dt(new Date(min).toISOString())} → {dt(new Date(max).toISOString())}). Not a Primavera replacement.</p>

      {/* Desktop Gantt */}
      <div className="hidden md:block bg-white rounded-xl border border-slate-200 p-4 overflow-x-auto" data-testid="timeline-gantt">
        <div className="space-y-1 min-w-[640px]">
          {acts.map((a) => {
            const s = parse(a.plannedStart); const f = parse(a.plannedFinish);
            return (
              <div key={a.id} onClick={() => onOpenActivity(a.id)} className="flex items-center gap-3 hover:bg-slate-50 rounded cursor-pointer py-1" data-testid="timeline-row">
                <div className="w-52 shrink-0 truncate text-xs text-slate-700 pl-1">{a.activityCode ? `${a.activityCode} · ` : ''}{a.activityName}</div>
                <div className="relative flex-1 h-5">
                  <div className="absolute inset-y-0 left-0 right-0 bg-slate-50 rounded" />
                  {a.milestone && s ? (
                    <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 text-purple-600" style={{ left: pct(s) }} title="Milestone"><Diamond className="w-3.5 h-3.5 fill-purple-600" /></div>
                  ) : s && f ? (
                    <div className={`absolute inset-y-0.5 rounded ${STATUS_BAR[a.status] || STATUS_BAR.UNKNOWN}`} style={{ left: pct(s), width: `calc(${pct(f)} - ${pct(s)} + 4px)` }} title={`${dt(a.plannedStart)} → ${dt(a.plannedFinish)}`} />
                  ) : (
                    <div className="absolute inset-y-0 left-0 text-[10px] text-slate-300 pl-1">no dates</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Mobile list */}
      <div className="md:hidden space-y-2" data-testid="timeline-mobile-list">
        {[...acts].sort((a, b) => String(a.plannedStart || '').localeCompare(String(b.plannedStart || ''))).map((a) => (
          <div key={a.id} onClick={() => onOpenActivity(a.id)} className="bg-white rounded-lg border border-slate-200 p-3" data-testid="timeline-mobile-row">
            <div className="flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full ${STATUS_BAR[a.status] || STATUS_BAR.UNKNOWN}`} />
              <p className="text-sm font-medium text-slate-800 truncate flex-1">{a.activityName}</p>
              {a.milestone && <Diamond className="w-3 h-3 text-purple-600 fill-purple-600" />}
            </div>
            <p className="text-xs text-slate-500 mt-1">{dt(a.plannedStart)} → {dt(a.plannedFinish)}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ScheduleTimeline;
